import { useCallback, useEffect, useMemo, useRef, useState } from "react"; // Attention!!! useState was removed before to solve query-sharing issues; now re-added only for loading backend stats.
import { SearchFocusTarget, User } from "@/App";
import type { NavigateOptions } from "@/app/types";
import { buildSearchQueryString } from "@/app/searchNavigation";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import {
  Search,
  Loader2,
  Globe,
  FolderTree,
  TrendingUp,
  Users,
  Database,
  ArrowRight,
  Lightbulb,
} from "lucide-react";
import { useApp } from "@/contexts/AppContext";
import { useAuth } from "@/contexts/AuthContext";
import { userAccountService } from "@/services/userAccount.service";
import unauthenticatedApi from "@/utils/unauthenticatedAxios";
import { getMlSmartSearchEnabled } from "@/services/publicSystemSettings.service";
import { resolveSmartSearchIntent } from "@/services/smartSearch.service";
import { Skeleton } from "./ui/skeleton";

interface HomeProps {
  navigate: (page: string, options?: NavigateOptions) => void;
  currentUser: User | null;
  onLogout: () => void;
  onSearch: (query: string) => void;
  onNavigateToSearchPage: (target: SearchFocusTarget) => void;
}

type HomeStatsDto = {
  totalWebsites: number;
  totalCategories: number;
  totalUsers: number;
};

const HOME_STATS_API_URL = "/public/statistics/home-summary";
const HOME_AI_SEARCH_STORAGE_KEY = "home.aiSearchEnabled";
const HOME_AI_SUGGESTION_INTERVAL_MS = 4200;

const readHomeAiSearchPreference = (): boolean => {
  if (typeof window === "undefined") return true;
  try {
    return window.localStorage.getItem(HOME_AI_SEARCH_STORAGE_KEY) !== "false";
  } catch {
    return true;
  }
};

const persistHomeAiSearchPreference = (enabled: boolean) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(HOME_AI_SEARCH_STORAGE_KEY, String(enabled));
  } catch {
    // Ignore localStorage write errors.
  }
};

export function Home({
  navigate,
  currentUser,
  onLogout,
  onSearch,
  onNavigateToSearchPage,
}: HomeProps) {
  const { t, language, searchQuery, setSearchQuery } = useApp();
  const { user, isAuthenticated, updateHomeAiSearchEnabled } = useAuth();

  // Backend-driven stats for the 3 quick cards.
  const [stats, setStats] = useState<HomeStatsDto>({
    totalWebsites: 0,
    totalCategories: 0,
    totalUsers: 0,
  });
  const [statsLoading, setStatsLoading] = useState<boolean>(true);
  const [mlSmartSearchEnabled, setMlSmartSearchEnabled] = useState(true);
  const [aiSearchEnabled, setAiSearchEnabled] = useState<boolean>(
    () => readHomeAiSearchPreference()
  );
  const [intentLoading, setIntentLoading] = useState(false);
  const [aiSuggestionIndex, setAiSuggestionIndex] = useState(0);
  const syncKeyRef = useRef<string>("");
  const isGermanLocale = language === "de";

  const aiSuggestionPlaceholders = useMemo(
    () =>
      isGermanLocale
        ? [
            t("home.aiSuggestionDistrictPairDe"),
            t("home.aiSuggestionVicinityDe"),
            t("home.aiSuggestionMacroRegionDe"),
            t("home.aiSuggestionBrandDe"),
            t("home.aiSuggestionMixedDeEn"),
            t("home.aiSuggestionTyposDe"),
          ]
        : [
            t("home.aiSuggestionDistrictPairEn"),
            t("home.aiSuggestionVicinityEn"),
            t("home.aiSuggestionMacroRegionEn"),
            t("home.aiSuggestionBrandEn"),
            t("home.aiSuggestionMixedDeEn"),
          ],
    [isGermanLocale, t]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    persistHomeAiSearchPreference(aiSearchEnabled);
  }, [aiSearchEnabled]);

  useEffect(() => {
    let cancelled = false;
    const loadSetting = async () => {
      const enabled = await getMlSmartSearchEnabled();
      if (cancelled) return;
      setMlSmartSearchEnabled(enabled);
    };
    loadSetting();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const localPreference = readHomeAiSearchPreference();

    if (!isAuthenticated || !user?.id) {
      if (syncKeyRef.current === "guest") {
        return;
      }
      syncKeyRef.current = "guest";
      setAiSearchEnabled(localPreference);
      return;
    }

    const syncKey = `${user.id}:${String(user.homeAiSearchEnabled)}`;
    if (syncKeyRef.current === syncKey) {
      return;
    }
    syncKeyRef.current = syncKey;

    const syncPreference = async () => {
      let resolved = localPreference;

      if (typeof user.homeAiSearchEnabled === "boolean") {
        resolved = user.homeAiSearchEnabled;
      } else {
        try {
          const response = await userAccountService.getHomeAiSearchPreference();
          if (cancelled) return;
          updateHomeAiSearchEnabled(response.enabled);
          if (typeof response.enabled === "boolean") {
            resolved = response.enabled;
          } else {
            const backfillResponse =
              await userAccountService.updateHomeAiSearchPreference({
                enabled: localPreference,
              });
            if (cancelled) return;
            updateHomeAiSearchEnabled(backfillResponse.enabled);
            if (typeof backfillResponse.enabled === "boolean") {
              resolved = backfillResponse.enabled;
            }
          }
        } catch {
          resolved = localPreference;
        }
      }

      if (cancelled) return;
      setAiSearchEnabled(resolved);
    };

    void syncPreference();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, updateHomeAiSearchEnabled, user?.homeAiSearchEnabled, user?.id]);

  const handleAiSearchToggle = useCallback(
    (checked: boolean) => {
      const nextEnabled = Boolean(checked);
      setAiSearchEnabled(nextEnabled);
      persistHomeAiSearchPreference(nextEnabled);

      if (!isAuthenticated || !user?.id) {
        return;
      }

      updateHomeAiSearchEnabled(nextEnabled);
      void userAccountService
        .updateHomeAiSearchPreference({ enabled: nextEnabled })
        .then((response) => {
          updateHomeAiSearchEnabled(response.enabled);
          if (typeof response.enabled === "boolean") {
            setAiSearchEnabled(response.enabled);
            persistHomeAiSearchPreference(response.enabled);
          }
        })
        .catch(() => {
          // Keep local state on API failure; Home search still works with local persistence.
        });
    },
    [isAuthenticated, updateHomeAiSearchEnabled, user?.id]
  );

  const effectiveAiSearchEnabled = mlSmartSearchEnabled && aiSearchEnabled;
  const aiSearchStatus = !mlSmartSearchEnabled
    ? "forced-off"
    : effectiveAiSearchEnabled
      ? "on"
      : "off";

  const aiSearchStatusLabel =
    aiSearchStatus === "on"
      ? t("home.aiSearchStatusOn")
      : aiSearchStatus === "forced-off"
        ? t("home.aiSearchStatusForcedOff")
        : t("home.aiSearchStatusOff");

  const aiSearchStatusHint =
    aiSearchStatus === "on"
      ? t("home.aiSearchStatusHintOn")
      : aiSearchStatus === "forced-off"
        ? t("home.aiSearchStatusHintForcedOff")
        : t("home.aiSearchStatusHintOff");

  const aiSearchStatusBadgeClass =
    aiSearchStatus === "on"
      ? "bg-green-100 text-green-800 border-green-300"
      : aiSearchStatus === "forced-off"
        ? "bg-amber-100 text-amber-800 border-amber-300"
        : "bg-slate-200 text-slate-700 border-slate-300";

  useEffect(() => {
    if (aiSuggestionPlaceholders.length === 0) return;
    setAiSuggestionIndex(Math.floor(Math.random() * aiSuggestionPlaceholders.length));
  }, [aiSuggestionPlaceholders]);

  useEffect(() => {
    if (
      !effectiveAiSearchEnabled ||
      searchQuery.trim().length > 0 ||
      aiSuggestionPlaceholders.length === 0
    ) {
      return;
    }

    const interval = window.setInterval(() => {
      setAiSuggestionIndex((prev) => {
        if (aiSuggestionPlaceholders.length <= 1) {
          return 0;
        }
        let next = prev;
        while (next === prev) {
          next = Math.floor(Math.random() * aiSuggestionPlaceholders.length);
        }
        return next;
      });
    }, HOME_AI_SUGGESTION_INTERVAL_MS);

    return () => {
      window.clearInterval(interval);
    };
  }, [effectiveAiSearchEnabled, searchQuery, aiSuggestionPlaceholders]);

  // Load quick stats from backend.
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setStatsLoading(true);
      try {
        const res =
          await unauthenticatedApi.get<HomeStatsDto>(HOME_STATS_API_URL);
        if (cancelled) return;

        setStats({
          totalWebsites: Number(res.data?.totalWebsites ?? 0),
          totalCategories: Number(res.data?.totalCategories ?? 0),
          totalUsers: Number(res.data?.totalUsers ?? 0),
        });
      } catch {
        // Keep defaults (0) if backend is not reachable.
        if (cancelled) return;
        setStats((prev) => prev);
      } finally {
        if (cancelled) return;
        setStatsLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedQuery = searchQuery.trim();

    if (!trimmedQuery) {
      onSearch("");
      return;
    }

    if (!effectiveAiSearchEnabled) {
      onSearch(trimmedQuery);
      return;
    }

    setIntentLoading(true);
    try {
      const intent = await resolveSmartSearchIntent(
        trimmedQuery,
        language === "de" ? "de" : "en"
      );

      const resolvedQuery = (intent.textQuery ?? "").trim();
      const effectiveDisplayQuery = resolvedQuery;
      const queryString = buildSearchQueryString({
        query: effectiveDisplayQuery,
        regions: intent.regionKeys,
        categorySlugs: intent.categorySlugs,
      });

      if (!queryString) {
        onSearch(trimmedQuery);
        return;
      }

      setSearchQuery(effectiveDisplayQuery);
      navigate("search", {
        search: queryString,
        state: {
          searchQuery: effectiveDisplayQuery,
          focusTarget: "search",
        },
      });
    } catch {
      // Silent fallback requested by product requirements.
      onSearch(trimmedQuery);
    } finally {
      setIntentLoading(false);
    }
  };

  const totalWebsites = stats.totalWebsites ?? 0;
  const totalCategories = stats.totalCategories ?? 0;
  const totalUsers = stats.totalUsers ?? 0;
  const homeIconStyle = {
    color:
      "color-mix(in srgb, color-mix(in srgb, var(--primary) 70%, var(--accent)) 90%, var(--foreground))",
  } as const;
  const featureTitleStyle = {
    color: "color-mix(in srgb, var(--foreground) 77%, var(--surface))",
  } as const;
  const featureDescriptionStyle = {
    color: "color-mix(in srgb, var(--foreground) 66%, var(--surface))",
  } as const;
  const resolvedPlaceholder =
    effectiveAiSearchEnabled && !searchQuery.trim()
      ? aiSuggestionPlaceholders[aiSuggestionIndex] || t("common.searchPlaceholder")
      : t("common.searchPlaceholder");

  return (
    <div className="flex flex-col min-h-screen">
      <Header
        navigate={navigate}
        currentUser={currentUser}
        onLogout={onLogout}
        currentPage="home"
        samePageHandlers={{
          home: () =>
            typeof window !== "undefined" &&
            window.scrollTo({ top: 0, behavior: "smooth" }),
        }}
        onNavigateToSearchPage={onNavigateToSearchPage}
      />

      <main className="flex-1">
        {/* Hero Section */}
        <section
          className="py-20"
          style={{
            backgroundColor: "var(--background)",
            backgroundImage:
              "linear-gradient(135deg, color-mix(in srgb, var(--primary) 12%, var(--background)), color-mix(in srgb, var(--surface) 70%, var(--background)))",
          }}
        >
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-primary-heading mb-4">
                {t("home.heroTitle")}
              </h2>
              <p className="text-secondary mb-8 max-w-2xl mx-auto">
                {t("home.heroDescription")}
              </p>

              {/* Search Bar */}
              <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
                <label htmlFor="home-search-input" className="sr-only">
                  {t("common.searchPlaceholder")}
                </label>
                <div
                  className="flex gap-2 p-2 rounded-lg shadow-lg"
                  style={{ backgroundColor: "var(--surface)" }}
                >
                  <Input
                    id="home-search-input"
                    name="homeSearch"
                    type="text"
                    placeholder={resolvedPlaceholder}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="border-0 focus-visible:ring-0 flex-1"
                  />
                  <Button type="submit" className="gap-2" disabled={intentLoading}>
                    {intentLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                    ) : (
                      <Search className="w-4 h-4" aria-hidden="true" />
                    )}
                    {t("home.search")}
                  </Button>
                </div>
                {mlSmartSearchEnabled ? (
                  <>
                    <div className="mt-3">
                      <button
                        type="button"
                        onClick={() => {
                          handleAiSearchToggle(!aiSearchEnabled);
                        }}
                        className="w-full rounded-md border px-4 py-3 text-left transition-colors hover:bg-[color-mix(in_srgb,var(--surface)_65%,var(--background))]"
                        aria-pressed={effectiveAiSearchEnabled}
                        aria-label={t("home.aiSearchToggleLabel")}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold text-primary-heading">
                              {t("home.aiSearchToggleLabel")}
                            </p>
                            <p className="text-xs text-secondary mt-1">{aiSearchStatusHint}</p>
                          </div>
                          <Badge className={aiSearchStatusBadgeClass}>{aiSearchStatusLabel}</Badge>
                        </div>
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-secondary text-center">
                      {t("home.aiSearchToggleHint")}
                    </p>
                  </>
                ) : null}
              </form>

              <div className="flex flex-wrap justify-center gap-4 mt-6">
                <Button
                  variant="outline"
                  onClick={() => navigate("categories")}
                  className="gap-2 text-secondary"
                >
                  <FolderTree className="w-4 h-4" />
                  {t("home.browseCategories")}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate("statistics")}
                  className="gap-2 text-secondary"
                >
                  <TrendingUp className="w-4 h-4" />
                  {t("home.viewStatistics")}
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Stats */}
        <section
          className="py-12"
          style={{ backgroundColor: "var(--background)" }}
        >
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <Card className="stat-card">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="icon-box">
                      <Globe
                        className="w-6 h-6 icon-primary"
                        style={homeIconStyle}
                      />
                    </div>
                    <div>
                      {statsLoading ? (
                        <>
                          <Skeleton className="h-4 w-28" />
                          <Skeleton className="h-8 w-24 mt-2" />
                        </>
                      ) : (
                        <>
                          <p className="text-secondary text-sm">
                            {t("home.statsWebsites")}
                          </p>
                          <p className="text-primary-heading">
                            {totalWebsites.toLocaleString("de-DE")}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="stat-card">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="icon-box">
                      <FolderTree
                        className="w-6 h-6 icon-primary"
                        style={homeIconStyle}
                      />
                    </div>
                    <div>
                      {statsLoading ? (
                        <>
                          <Skeleton className="h-4 w-32" />
                          <Skeleton className="h-8 w-24 mt-2" />
                        </>
                      ) : (
                        <>
                          <p className="text-secondary text-sm">
                            {t("home.statsCategories")}
                          </p>
                          <p className="text-primary-heading">
                            {totalCategories.toLocaleString("de-DE")}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="stat-card">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="icon-box">
                      <Users
                        className="w-6 h-6 icon-primary"
                        style={homeIconStyle}
                      />
                    </div>
                    <div>
                      {statsLoading ? (
                        <>
                          <Skeleton className="h-4 w-24" />
                          <Skeleton className="h-8 w-24 mt-2" />
                        </>
                      ) : (
                        <>
                          <p className="text-secondary text-sm">
                            {t("home.statsUsers")}
                          </p>
                          <p className="text-primary-heading">
                            {totalUsers.toLocaleString("de-DE")}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Features */}
        <section
          className="py-16"
          style={{
            backgroundColor:
              "color-mix(in srgb, var(--surface) 70%, var(--background))",
          }}
        >
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h3 className="text-primary-heading mb-3">
                {t("home.featuresTitle")}
              </h3>
              <p className="text-secondary max-w-2xl mx-auto">
                {t("home.featuresDescription")}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              <Card className="feature-card">
                <CardHeader className="py-8">
                  <div className="icon-box w-fit mb-2">
                    <Search
                      className="w-6 h-6 icon-primary"
                      style={homeIconStyle}
                    />
                  </div>
                  <CardTitle
                    className="card-title-text"
                    style={featureTitleStyle}
                  >
                    {t("home.featureAdvancedSearch")}
                  </CardTitle>
                  <CardDescription
                    className="card-description-text"
                    style={featureDescriptionStyle}
                  >
                    {t("home.featureAdvancedSearchDesc")}
                  </CardDescription>
                </CardHeader>
              </Card>

              <Card className="feature-card">
                <CardHeader className="py-8">
                  <div className="icon-box w-fit mb-2">
                    <FolderTree
                      className="w-6 h-6 icon-primary"
                      style={homeIconStyle}
                    />
                  </div>
                  <CardTitle
                    className="card-title-text"
                    style={featureTitleStyle}
                  >
                    {t("home.featureCategorization")}
                  </CardTitle>
                  <CardDescription
                    className="card-description-text"
                    style={featureDescriptionStyle}
                  >
                    {t("home.featureCategorizationDesc")}
                  </CardDescription>
                </CardHeader>
              </Card>

              <Card className="feature-card">
                <CardHeader className="py-8">
                  <div className="icon-box w-fit mb-2">
                    <Users
                      className="w-6 h-6 icon-primary"
                      style={homeIconStyle}
                    />
                  </div>
                  <CardTitle
                    className="card-title-text"
                    style={featureTitleStyle}
                  >
                    {t("home.featureCollaboration")}
                  </CardTitle>
                  <CardDescription
                    className="card-description-text"
                    style={featureDescriptionStyle}
                  >
                    {t("home.featureCollaborationDesc")}
                  </CardDescription>
                </CardHeader>
              </Card>

              <Card className="feature-card">
                <CardHeader className="py-8">
                  <div className="icon-box w-fit mb-2">
                    <Database
                      className="w-6 h-6 icon-primary"
                      style={homeIconStyle}
                    />
                  </div>
                  <CardTitle
                    className="card-title-text"
                    style={featureTitleStyle}
                  >
                    {t("home.featureLongTermArchiving")}
                  </CardTitle>
                  <CardDescription
                    className="card-description-text"
                    style={featureDescriptionStyle}
                  >
                    {t("home.featureLongTermArchivingDesc")}
                  </CardDescription>
                </CardHeader>
              </Card>

              <Card className="feature-card">
                <CardHeader className="py-8">
                  <div className="icon-box w-fit mb-2">
                    <TrendingUp
                      className="w-6 h-6 icon-primary"
                      style={homeIconStyle}
                    />
                  </div>
                  <CardTitle
                    className="card-title-text"
                    style={featureTitleStyle}
                  >
                    {t("home.featureStatistics")}
                  </CardTitle>
                  <CardDescription
                    className="card-description-text"
                    style={featureDescriptionStyle}
                  >
                    {t("home.featureStatisticsDesc")}
                  </CardDescription>
                </CardHeader>
              </Card>

              <Card className="feature-card">
                <CardHeader className="py-8">
                  <div className="icon-box w-fit mb-2">
                    <Globe
                      className="w-6 h-6 icon-primary"
                      style={homeIconStyle}
                    />
                  </div>
                  <CardTitle
                    className="card-title-text"
                    style={featureTitleStyle}
                  >
                    {t("home.featurePublicAccess")}
                  </CardTitle>
                  <CardDescription
                    className="card-description-text"
                    style={featureDescriptionStyle}
                  >
                    {t("home.featurePublicAccessDesc")}
                  </CardDescription>
                </CardHeader>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        {!currentUser && (
          <section
            className="py-16"
            style={{
              backgroundColor: "var(--background)",
              backgroundImage:
                "linear-gradient(135deg, color-mix(in srgb, var(--primary) 22%, var(--background)), color-mix(in srgb, var(--accent) 18%, var(--background)))",
            }}
          >
            <div className="container mx-auto px-4 text-center">
              <h3 className="text-primary-heading mb-4">
                {t("home.ctaTitle")}
              </h3>
              <p className="text-secondary mb-8 max-w-2xl mx-auto">
                {t("home.ctaDescription")}
              </p>
              <div className="flex gap-4 justify-center flex-wrap">
                <Button onClick={() => navigate("register")} size="lg">
                  {t("home.ctaRegister")}
                  <ArrowRight className="w-4 h-4" />
                </Button>
                <Button
                  onClick={() => navigate("suggest-website")}
                  size="lg"
                  variant="outline"
                  className="gap-2"
                >
                  {t("home.ctaSuggest")}
                  <Lightbulb className="w-4 h-4" />
                </Button>
                <Button
                  onClick={() => navigate("login")}
                  size="lg"
                  variant="outline"
                >
                  {t("home.ctaLogin")}
                </Button>
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer navigate={navigate} />
    </div>
  );
}
